// FR overlays for content that lives in lib/data.js (SOLUTIONS, INDUSTRIES, COMPARE_LIGHTSPEED, HARDWARE).
// English remains the source of truth in data.js; when locale === "fr", we merge these strings.
import { SOLUTIONS, INDUSTRIES, COMPARE_LIGHTSPEED, HARDWARE } from "../lib/data";

const HARDWARE_FR = {
  "OJ-POS-15": "Écran de caisse 15\"",
  "OJ-PAY-S1F2": "Terminal de paiement S1F2 (avec imprimante)",
  "OJ-PAY-AMS1": "Terminal de paiement AMS1 (sans imprimante)",
  "OJ-HANDHELD": "Tablette serveur",
  "OJ-RCPT-80": "Imprimante de tickets 80 mm",
  "OJ-CASH": "Caisse-tiroir",
  "OJ-BALANCE": "Balance",
  "OJ-KIOSK-238": "Borne en libre-service 23,8\"",
  "OJ-CDS": "Customer Display System (CDS) 10\"",
  "OJ-KDS-156": "Kitchen Display System (KDS) 15,6\"",
};

const SOLUTIONS_FR = {
  pos: {
    name: "Caisse enregistreuse",
    short: "POS",
    tagline: "Vendez plus vite. Clôturez plus net.",
    description:
      "Une caisse cloud ultra-rapide pour la restauration et le commerce, avec mode hors-ligne, multi-postes, Kitchen Display (KDS), Customer Display (CDS), saisie centre d'appel, intégration UberEats et paiements intégrés.",
    bullets: [
      "Saisie d'une commande en moins d'une seconde",
      "Offline-first — aucune vente ratée",
      "Multi-postes + multi-sites",
      "Kitchen Display System (KDS)",
      "Customer Display System (CDS)",
      "Saisie centre d'appel",
      "Intégration UberEats",
      "Paiements & pourboires intégrés",
    ],
  },
  kiosk: {
    name: "Borne en libre-service",
    short: "BORNE",
    tagline: "Réduisez les files. +22 % de ticket moyen.",
    description:
      "Bornes 23,8\" autoportantes ou de comptoir : commandes, TWINT + cartes, et routage direct vers la cuisine.",
    bullets: [
      "Matériel 23,8\", comptoir ou autoportant",
      "TWINT, Apple Pay, Google Pay, EMV",
      "Routage direct vers la cuisine",
      "Offert sous éligibilité commerçant",
    ],
  },
  "waiter-app": {
    name: "App serveur",
    short: "Serveur",
    tagline: "La prise de commande à table qui fonctionne vraiment.",
    description:
      "Prenez les commandes, envoyez les plats et divisez l'addition depuis n'importe quel mobile — même si le Wi-Fi lâche.",
    bullets: ["iOS & Android", "Envoi par plats", "Division & fusion des additions", "Pool de pourboires prêt"],
  },
  "qr-order-pay": {
    name: "Commande & paiement QR",
    short: "QR",
    tagline: "Scannez. Commandez. Repartez.",
    description:
      "Menus QR personnalisés qui transforment chaque table en canal de vente en libre-service.",
    bullets: ["Menus brandés", "Sans téléchargement", "Pourboire automatique", "Compatible fidélité"],
  },
  "online-shop": {
    name: "Boutique en ligne",
    short: "Shop",
    tagline: "Votre vitrine. Vos marges.",
    description:
      "Commande en ligne sans commission, avec livraison native, retrait et paiement Swisspayout.",
    bullets: ["Zéro commission", "Paiement Swisspayout", "Zones de livraison natives", "Optimisé SEO"],
  },
  "payment-terminals": {
    name: "Terminaux de paiement",
    short: "Paiement",
    tagline: "Un seul appareil. Tous les paiements.",
    description:
      "EMV, sans contact, TWINT, Apple Pay et Google Pay — entièrement intégrés à la caisse via Swisspayout, sans double saisie. Au choix : filaire (S1F2) ou autonome avec SIM intégrée (AMS1).",
    bullets: ["Terminal filaire S1F2", "AMS1 avec SIM intégrée", "TWINT pris en charge", "Taux spécial 0,90 % avec nos terminaux"],
  },
  "gift-cards-loyalty": {
    name: "Cartes cadeaux & Fidélité",
    short: "Fidélité",
    tagline: "Transformez une visite en dix.",
    description:
      "Adhésions, points de fidélité, cartes cadeaux physiques et numériques en un clic — toutes adossées à la même caisse qui a encaissé la vente.",
    bullets: [
      "Adhésions & programme de fidélité par paliers",
      "Moteur de points, tampons & récompenses",
      "Vente de carte cadeau en un clic depuis toute caisse",
      "Cartes cadeaux personnalisées",
      "Cartes cadeaux numériques par e-mail & SMS",
      "Échange automatique en caisse, borne et en ligne",
    ],
  },
  inventory: {
    name: "Gestion des stocks",
    short: "Stocks",
    tagline: "Maîtrisez votre stock. Tenez votre salle.",
    description:
      "Stock SKU en temps réel, contrôle des recettes et coûts, commandes fournisseurs et suivi des pertes — connectés à tous les canaux.",
    bullets: [
      "Stock SKU en temps réel",
      "Recettes & coût matière",
      "Commandes fournisseurs & réceptions",
      "Suivi des pertes & déchets",
      "Transferts de stock multi-sites",
      "Alertes seuils bas & réassort auto",
    ],
  },
};

const INDUSTRIES_FR = {
  "quick-service": {
    name: "Restauration rapide (QSR)",
    blurb: "Service au comptoir rapide, bornes et paiement sans file.",
    points: ["Borne en libre-service", "Tickets cuisine directs", "Fidélité + cartes cadeaux", "Mobile drive"],
  },
  "delivery-service": {
    name: "Service de livraison",
    blurb: "Livraison native + UberEats, le tout depuis une seule caisse.",
    points: ["Intégration UberEats", "Boutique en ligne & QR", "Affectation des livreurs", "Tarification par zone"],
  },
  restaurants: {
    name: "Restaurants & Cafés",
    blurb: "Commande à table, envoi par plats, pool de pourboires prêt.",
    points: ["App serveur", "Envoi par plats", "Kitchen Display", "Customer Display"],
  },
  "grocery-stores": {
    name: "Épicerie & Proximité",
    blurb: "Stocks au SKU, balances et workflows code-barres.",
    points: ["Gestion des stocks", "Code-barres & balance", "Promos & règles multi-buy", "Adhésions fidélité"],
  },
  "food-truck": {
    name: "Food truck",
    blurb: "Terminaux SIM, caisse hors-ligne, rapports de fin de service.",
    points: ["Caisse offline-first", "Terminal AMS1 avec SIM", "Comptes équipage", "Rapports de fin de service"],
  },
  franchise: {
    name: "Franchise & Multi-marque",
    blurb: "Déploiements multi-marques et multi-régions avec contrôle centralisé.",
    points: ["Tableau de bord multi-sites", "Menus verrouillés par marque", "Reporting redevances", "Tarifs & promos centralisés"],
  },
};

const COMPARE_LIGHTSPEED_FR = [
  { dim: "Tarif mensuel (Starter)", oj: "CHF 39 avec notre terminal", ls: "CHF 100+ avant options" },
  { dim: "Bundle matériel", oj: "Offert pour les commerçants éligibles", ls: "Payant, souvent 2–3 k CHF" },
  { dim: "Taux carte", oj: "0,90 % avec les terminaux O'javana", ls: "1,5–2,0 % en moyenne" },
  { dim: "Délai d'installation", oj: "Sous 48 heures", ls: "1 à 4 semaines" },
  { dim: "Réactivité du support", oj: "Dans l'heure / le jour-même", ls: "Files de tickets, rappels" },
  { dim: "Où c'est conçu", oj: "Né à Neuchâtel · Made in Switzerland", ls: "Global, sans présence locale" },
  { dim: "Périmètre produit", oj: "Compact, restauration + retail", ls: "Beaucoup de SKUs, beaucoup d'upsell" },
  { dim: "TWINT", oj: "Natif", ls: "Via un add-on partenaire" },
];

export function getSolution(slug, locale) {
  const base = SOLUTIONS.find((s) => s.slug === slug);
  if (!base) return null;
  if (locale === "fr" && SOLUTIONS_FR[slug]) {
    return { ...base, ...SOLUTIONS_FR[slug] };
  }
  return base;
}

export function getSolutions(locale) {
  return SOLUTIONS.map((s) => (locale === "fr" && SOLUTIONS_FR[s.slug] ? { ...s, ...SOLUTIONS_FR[s.slug] } : s));
}

export function getIndustries(locale) {
  return INDUSTRIES.map((i) => (locale === "fr" && INDUSTRIES_FR[i.slug] ? { ...i, ...INDUSTRIES_FR[i.slug] } : i));
}

export function getCompareLightspeed(locale) {
  if (locale === "fr") {
    return COMPARE_LIGHTSPEED.map((row, idx) => ({ ...row, ...COMPARE_LIGHTSPEED_FR[idx] }));
  }
  return COMPARE_LIGHTSPEED;
}

export function getHardware(locale) {
  if (locale === "fr") {
    return HARDWARE.map((h) => ({ ...h, name: HARDWARE_FR[h.sku] || h.name }));
  }
  return HARDWARE;
}
